import type { ResolvedDefinition, SectionKey, SubmissionPayload } from '@y365/shared';

/**
 * Typed client for apps/api.
 *
 * The token lives in the URL path only. Everything after the OTP exchange runs
 * on a session cookie, so the token never appears in a fetch we make and never
 * ends up in a query string — see docs/security.md.
 */

export interface SectionState {
  sectionKey: SectionKey;
  title: string;
  status: 'notSent' | 'sent' | 'inProgress' | 'submitted';
  /** ISO date of the last submission, or null if never submitted. */
  submittedOn: string | null;
  /** 0 if never submitted. */
  revision: number;
  definition: ResolvedDefinition;
  values: Record<string, unknown>;
}

export interface SessionState {
  onboardingId: string;
  customerName: string;
  recipientName: string;
  recipientEmail: string;
  sections: SectionState[];
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    let message = `Something went wrong (${response.status}).`;
    let code: string | undefined;
    try {
      const body = (await response.json()) as { message?: string; code?: string };
      if (body.message) message = body.message;
      code = body.code;
    } catch {
      // Non-JSON error body. Keep the generic message rather than showing the
      // customer an HTML error page fragment.
    }
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Validate the link and find out whether a one-time code is needed. */
export function openInvite(token: string) {
  return request<{ needsCode: boolean; emailHint: string }>(
    `/s/${encodeURIComponent(token)}`,
  );
}

/** Exchange the emailed code for a session cookie. */
export function verifyCode(token: string, code: string) {
  return request<{ ok: true }>(`/s/${encodeURIComponent(token)}/verify`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** Every section on this invite, with its frozen definition and current values. */
export function getSession() {
  return request<SessionState>('/session/sections');
}

export function saveDraft(sectionKey: SectionKey, values: Record<string, unknown>) {
  return request<{ savedAt: string }>(
    `/session/sections/${encodeURIComponent(sectionKey)}/draft`,
    { method: 'PUT', body: JSON.stringify({ values }) },
  );
}

export interface SubmitResult {
  ok: boolean;
  revision?: number;
  errors?: Array<{ path: string; message: string }>;
}

export function submitSection(sectionKey: SectionKey, payload: SubmissionPayload) {
  return request<SubmitResult>(
    `/session/sections/${encodeURIComponent(sectionKey)}/submit`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}
